/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {motion, AnimatePresence} from 'framer-motion'

// Components
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    Text,
    Flex,
    useMultiStyleConfig,
    IconButton,
} from '@chakra-ui/react'
import {ChevronLeftIcon, CloseIcon} from '../icons'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useLocation} from 'react-router-dom'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {DrawerMenuSecondLevel} from './drawer-menu-second-level'
import {DrawerMenuFirstLevel} from './drawer-menu-first-level'
import {remCalc} from '../../theme/_utils'
import {BRAND_LABEL} from '../../brand'
import BrandLogo from '../icons/shared/logo'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */

const DrawerMenu = ({isOpen, onClose = noop, onWishlistClick}) => {
    const [ariaBusy, setAriaBusy] = useState('true')
    const [selectedPrimaryCategoryId, setSelectedPrimaryCategoryId] = useState(null)

    const intl = useIntl()
    const styles = useMultiStyleConfig('DrawerMenu')
    const {data} = useGetMainMenu()
    const {locale} = useMultiSite()
    const navigate = useNavigation()
    const firstLevelImageCardsData = [1, 2].map((slot) => {
        const assetType = data?.[`type_of_asset_${slot}`]
        const type = typeof assetType === 'string' ? assetType.toLowerCase() : undefined
        return {
            type,
            card: type === 'card' ? data?.[`card_${slot}`]?.[`card_${slot}`] : undefined,
            image: type === 'image' ? data?.[`image_${slot}`] : undefined,
        }
    })
    const onLogoClick = () => {
        navigate(`/${locale?.alias}`)
        onClose()
    }

    const location = useLocation()

    const categoriesData = useGetCategoriesData()

    const [hasSovraHeader, setHasSovraHeader] = useState(false)

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    useEffect(() => {
        if (typeof document === 'undefined') return
        setHasSovraHeader(Boolean(document.getElementById('sovra-header')))
    }, [isOpen])

    // remCalc must receive a literal: the px-to-rem babel plugin converts it at build time
    const topOffset = hasSovraHeader ? remCalc(24) : '0px'
    const drawerHeight = hasSovraHeader ? 'calc(100vh - 24px)' : '100vh'
    const drawerDynamicHeight = hasSovraHeader ? 'calc(100dvh - 24px)' : '100dvh'

    const locationKeyRef = useRef(location.key)
    locationKeyRef.current = location.key

    // Lock page scroll while the drawer is open. `position: fixed` on body is used
    // instead of only `overflow: hidden` because iOS Safari ignores the latter.
    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return

        const {body, documentElement: html} = document
        const scrollY = window.scrollY
        const lockedLocationKey = locationKeyRef.current
        const prevBody = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
        }
        const prevHtmlOverflow = html.style.overflow
        const prevHtmlOverscroll = html.style.overscrollBehavior

        body.style.position = 'fixed'
        body.style.top = `-${scrollY}px`
        body.style.left = '0'
        body.style.right = '0'
        body.style.width = '100%'
        body.style.overflow = 'hidden'
        html.style.overflow = 'hidden'
        html.style.overscrollBehavior = 'none'

        return () => {
            Object.assign(body.style, prevBody)
            html.style.overflow = prevHtmlOverflow
            html.style.overscrollBehavior = prevHtmlOverscroll
            // Only restore the previous position if the user didn't navigate away
            if (locationKeyRef.current === lockedLocationKey) {
                window.scrollTo(0, scrollY)
            }
        }
    }, [isOpen])

    //used to close the drawer every time the user navigates to another page
    useEffect(() => {
        if (isOpen) {
            onClose()
        }
    }, [location])

    const selectedCategory = data?.primarycategories.find(
        (primarycategory) => primarycategory.uid === selectedPrimaryCategoryId,
    )

    const categoryLink =
        categoriesData && getGlobalLink({link: selectedCategory?.redirects, categoriesData})

    const onDrawerClose = () => {
        setSelectedPrimaryCategoryId(null)
        onClose()
    }

    const variants = {
        initial: {x: '100%'},
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '100%', transition: {duration: 0.3}},
    }

    const reverseVariants = {
        initial: {x: '-100%'},
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '-100%', transition: {duration: 0.3}},
    }

    return (
        <Drawer
            isOpen={isOpen}
            // reset selectedId when drawer is closed from the parent component
            onCloseComplete={() => setSelectedPrimaryCategoryId(null)}
            onClose={onDrawerClose}
            placement="left"
            size="full"
            blockScrollOnMount={false} // page scroll is locked manually above (iOS-safe)
        >
            <DrawerOverlay width="full" h="full" style={{top: topOffset}} />
            <DrawerContent
                {...styles.drawerContent}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                w="100vw"
                maxW="100vw"
                style={{top: topOffset}}
                // Safari 15.4 and below doesn't support 100dvh, so we need to set the height to 100vh
                sx={{
                    height: drawerHeight,
                    maxHeight: drawerHeight,
                    '@supports (height: 100dvh)': {
                        height: drawerDynamicHeight,
                        maxHeight: drawerDynamicHeight,
                    },
                }}
            >
                <DrawerHeader
                    {...styles.drawerHeader}
                    aria-label={intl.formatMessage({
                        id: 'drawer_menu.header.assistive_msg.title',
                        defaultMessage: 'Menu Drawer',
                    })}
                >
                    <Flex
                        {...styles.breadcrumbContainer}
                        onClick={() => setSelectedPrimaryCategoryId(null)}
                    >
                        {selectedPrimaryCategoryId && (
                            <>
                                <ChevronLeftIcon boxSize="iconsSize.medium" />
                                <Text {...styles.breadcrumbText}>{categoryLink?.label}</Text>
                            </>
                        )}
                    </Flex>
                    <IconButton
                        aria-label={`${BRAND_LABEL} ${intl.formatMessage({
                            id: 'header.button.assistive_msg.logo',
                            defaultMessage: 'Logo',
                        })}`}
                        icon={<BrandLogo width={remCalc(72)} height={remCalc(32)} />}
                        variant="unstyled"
                        role="link"
                        tabIndex={0}
                        onClick={onLogoClick}
                        position="absolute"
                        left="50%"
                        top="50%"
                        transform="translate(-50%, -50%)"
                    />
                    <CloseIcon
                        onClick={onDrawerClose}
                        position="absolute"
                        right={remCalc(12)}
                        top="50%"
                        transform="translateY(-50%)"
                    />
                </DrawerHeader>
                <DrawerBody
                    {...styles.drawerBody}
                    px={
                        selectedCategory?._content_type_uid === 'menu_redesign_template_1'
                            ? {base: 'xsmall-1.base', md: 'small-1.base', lg: 'medium-1.base'}
                            : {base: 'xsmall-1.base', md: 'medium-1.md'}
                    }
                    sx={{
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain',
                        background: 'background.base.default',
                    }}
                    display="flex"
                    flexDir="column"
                    flex="1"
                    minH="0"
                    overflowY="auto"
                    onTouchMove={(e) => {
                        e.stopPropagation() // prevent event from reaching body
                    }}
                >
                    <Flex
                        id="category-nav"
                        aria-live="polite"
                        aria-busy={ariaBusy}
                        aria-atomic="true"
                        flex="1"
                        minH="0"
                        flexDir="column"
                    >
                        <AnimatePresence initial={false} mode="popLayout">
                            {data &&
                                categoriesData &&
                                (selectedCategory ? (
                                    <motion.div
                                        key="second-level"
                                        initial="initial"
                                        animate="enter"
                                        exit="exit"
                                        variants={variants}
                                        style={{height: '100%'}}
                                    >
                                        <DrawerMenuSecondLevel
                                            categoriesData={categoriesData}
                                            categoryUrl={categoryLink?.url}
                                            selectedCategory={selectedCategory}
                                            locale={locale}
                                        />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="first-level"
                                        initial="initial"
                                        animate="enter"
                                        exit="exit"
                                        variants={reverseVariants}
                                        style={{height: '100%'}}
                                    >
                                        <DrawerMenuFirstLevel
                                            primarycategories={data.primarycategories}
                                            firstLevelCardData={firstLevelImageCardsData}
                                            setSelectedPrimaryCategoryId={
                                                setSelectedPrimaryCategoryId
                                            }
                                            categoriesData={categoriesData}
                                            headerLinks={
                                                data?.headerlinks
                                                    ? data?.headerlinks?.[0]?.links
                                                    : []
                                            }
                                            onWishlistClick={onWishlistClick}
                                        />
                                    </motion.div>
                                ))}
                        </AnimatePresence>
                    </Flex>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    onWishlistClick: PropTypes.func,
}

export {DrawerMenu}
